/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useMemo } from 'react';
import { PlaidLinkStableEvent } from 'react-plaid-link';
import { WidgetConfigs } from 'aerosync-web-sdk';
import { getLogger } from 'src/utils/datadog-logger';
import _ from 'lodash';
import { useRouter } from 'next/compat/router';
import { useQuery } from '@apollo/client';

import { tracker, Events } from 'utils/analytics';
import { GetInstoreAnalyticsTokenDocument } from 'src/gql/graphql';
import {
  AeroSyncPreAuthEvent,
  AeroSyncPreAuthEventMetadata,
  DPAY_SESSION_TOKEN_KEY,
  EnrollmentSteps,
  LOG_NAMESPACES,
  PlaidLinkCustomizationNames,
} from 'src/payments/constants';
import { getPersistedValue, setPersistedValue } from 'shared/utils/persisted-values';
import { INSTORE_ENROLLMENT_ROUTE, INSTORE_ENROLLMENT_SOURCE } from 'src/components/instore-enrollment';

export enum ENROLLMENT_EVENTS {
  CTA_BUTTON_CLICKED = 'dutchiePayCTAButtonClicked',
  ENROLLMENT_BUTTON_CLICKED = 'dutchiePayEnrollmentButtonClicked',
  ENROLLMENT_STEP = 'dutchiePayEnrollmentStep',
  ENROLLMENT_STEP_CANCELLED = 'dutchiePayEnrollmentStepCancelled',
  PLAID_ON_EVENT = 'dutchiePayPlaidOnEvent',
  PLAID_ON_LOAD = 'dutchiePayPlaidOnLoad',
  PLAID_ON_SUCCESS = 'dutchiePayPlaidOnSuccess',
  PLAID_ON_EXIT = 'dutchiePayPlaidOnExit',
  AEROSYNC_ON_EVENT = 'dutchiePayAeroSyncOnEvent',
  AEROSYNC_PRE_AUTH_EVENT = 'dutchiePayPreAuthEvent',
}

export enum INSTORE_EVENTS {
  KIOSK_QR_CODE_MODAL_VIEWED = 'dutchiePayKioskQRCodeModalViewed',
  ACCOUNT_LINK_VIEWED = 'dutchiePayInstoreAccountLinkViewed',
  CART_APPROVAL_CLICKED = 'dutchiePayInstoreCartApprovalClicked',
  CART_APPROVAL_FAILED = 'dutchiePayInstoreCartApprovalFailed',
  CART_APPROVAL_SUCCEEDED = 'dutchiePayInstoreCartApprovalSucceeded',
  CART_VIEWED = 'dutchiePayInstoreCartViewed',
  LOGIN_VIEWED = 'dutchiePayInstoreLoginViewed',
}

export type DutchiePayEvents = ENROLLMENT_EVENTS | INSTORE_EVENTS;

export enum EVENT_ACTIONS {
  COMPLETED = 'Completed',
  FAILED = 'Failed',
  SUBMITTED = 'Submitted',
  SUCCEEDED = 'Succeeded',
  VIEWED = 'Viewed',
}

export enum GuestEnrollmentFunnelExperimentVariant {
  CONTROL = 'Control',
  EXPERIMENTAL = 'Experimental',
}

export enum BankConnectionContexts {
  BANK_CONNECTION_PLAID_EMBEDDED_SEARCH = 'plaid_embedded_search_widget',
  BANK_CONNECTION_PLAID_EMBEDDED_SEARCH_POPUP = 'plaid_embedded_search_widget_popup',
  BANK_CONNECTION_AEROSYNC = 'aerosync_widget',
  BANK_CONNECTION_AGGREGATOR = 'plaid_aggregator_widget',
  BANK_CONNECTION_PLAID_EMBEDDED_SEARCH_INSTORE = 'plaid_embedded_search_widget_instore',
  BANK_CONNECTION_PLAID_INCENTIVES = 'plaid_incentives',
}

export enum AuthContexts {
  AUTH_EMAIL_FORM = 'email_form',
  AUTH_EMAIL_PASSWORD = 'email_password_flow',
  AUTH_OAUTH = 'oauth_flow',
  AUTH_GOOGLE_LOGIN = 'google_login',
  AUTH_APPLE_LOGIN = 'apple_login',
}

export type EnrollmentContexts = AuthContexts | BankConnectionContexts;

export const PLAID_LINK_TO_BANK_CONNECTION_CONTEXT: Record<PlaidLinkCustomizationNames, BankConnectionContexts> = {
  [PlaidLinkCustomizationNames.DEFAULT]: BankConnectionContexts.BANK_CONNECTION_AGGREGATOR,
  [PlaidLinkCustomizationNames.EMBEDDED_SEARCH_ONLINE]: BankConnectionContexts.BANK_CONNECTION_PLAID_EMBEDDED_SEARCH,
  [PlaidLinkCustomizationNames.EMBEDDED_SEARCH_ONLINE_POPUP]:
    BankConnectionContexts.BANK_CONNECTION_PLAID_EMBEDDED_SEARCH_POPUP,
  [PlaidLinkCustomizationNames.EMBEDDED_SEARCH_INSTORE]:
    BankConnectionContexts.BANK_CONNECTION_PLAID_EMBEDDED_SEARCH_INSTORE,
  [PlaidLinkCustomizationNames.INCENTIVES]: BankConnectionContexts.BANK_CONNECTION_PLAID_INCENTIVES,
};

// Re-exported for existing import sites; canonical definition lives in src/payments/constants.
export { DPAY_SESSION_TOKEN_KEY };

export const ENROLLMENT_STEPS_TO_EXPERIMENT_METRICS = {
  [EnrollmentSteps.emailForm]: {
    [EVENT_ACTIONS.VIEWED]: 'fintech.pay-signup.page-viewed',
  },
  [EnrollmentSteps.connectBank]: {
    [EVENT_ACTIONS.VIEWED]: 'fintech.pay-signup.connect-bank-viewed',
    [EVENT_ACTIONS.SUCCEEDED]: 'fintech.pay-signup.connect-bank-succeeded',
  },
  [EnrollmentSteps.terms]: {
    [EVENT_ACTIONS.VIEWED]: 'fintech.pay-signup.terms-modal-viewed',
    [EVENT_ACTIONS.SUBMITTED]: 'fintech.pay-signup.terms-modal-accepted',
  },
  [EnrollmentSteps.completeEnrollment]: {
    [EVENT_ACTIONS.VIEWED]: 'fintech.pay-signup.enrollment-completed',
  },
  [EnrollmentSteps.alreadySignedUp]: {
    [EVENT_ACTIONS.VIEWED]: 'fintech.pay-signup.already-enrolled',
  },
};

export const PAY_SIGNUP_FUNNEL_STEPS = [
  'fintech.checkout.link-your-bank-clicked',
  'fintech.pay-signup.page-viewed',
  'fintech.pay-signup.connect-bank-viewed',
  'fintech.pay-signup.connect-bank-succeeded',
  'fintech.pay-signup.terms-modal-viewed',
  'fintech.pay-signup.terms-modal-accepted',
  'fintech.pay-signup.enrollment-completed',
  'fintech.checkout.pay-by-bank-order-submitted',
];

export type DutchiePayEventTracker = <K extends DutchiePayEvents>(
  methodName: K,
  ...args: Omit<Events[K], 'dutchiePaySessionToken' | 'instoreCartToken'> extends Record<string, never>
    ? [] // No second param if object is empty
    : [payloadProps: Omit<Events[K], 'dutchiePaySessionToken' | 'instoreCartToken'>] // Required if has props
) => void;

type EnrollmentStepViewedTracker = (props: { step: EnrollmentSteps; context?: EnrollmentContexts }) => void;
type EnrollmentStepSubmittedTracker = (props: {
  step: EnrollmentSteps;
  context?: EnrollmentContexts;
  action?: EVENT_ACTIONS;
}) => void;

type PlaidOnEventTracker = (payload: Omit<Events['dutchiePayPlaidOnEvent'], 'dutchiePaySessionToken'>) => void;
type PlaidOnLoadTracker = (payload: Omit<Events['dutchiePayPlaidOnLoad'], 'dutchiePaySessionToken'>) => void;
type PlaidOnSuccessTracker = (payload: Omit<Events['dutchiePayPlaidOnSuccess'], 'dutchiePaySessionToken'>) => void;

type PlaidOnExitTracker = (payload: Omit<Events['dutchiePayPlaidOnExit'], 'dutchiePaySessionToken'>) => void;

type AeroSyncOnEventTracker = WidgetConfigs['onEvent'];
type AeroSyncOnLoadTracker = WidgetConfigs['onLoad'];
type AeroSyncOnSuccessTracker = WidgetConfigs['onSuccess'];
type AeroSyncOnErrorTracker = WidgetConfigs['onError'];
type AeroSyncOnCloseTracker = WidgetConfigs['onClose'];
type PreAuthEventTracker = (eventName: AeroSyncPreAuthEvent, eventMetadata?: AeroSyncPreAuthEventMetadata) => void;

const logger = getLogger(LOG_NAMESPACES.PBB);

export type UseDutchiePayAnalyticsResponse = {
  dutchiePayEventTracker: DutchiePayEventTracker;
  trackEnrollmentStepSubmitted: EnrollmentStepSubmittedTracker;
  trackEnrollmentStepViewed: EnrollmentStepViewedTracker;
  trackPlaidOnEvent: PlaidOnEventTracker;
  trackPlaidOnLoad: PlaidOnLoadTracker;
  trackPlaidOnSuccess: PlaidOnSuccessTracker;
  trackPlaidOnExit: PlaidOnExitTracker;
  trackAeroSyncOnEvent: AeroSyncOnEventTracker;
  trackAeroSyncOnLoad: AeroSyncOnLoadTracker;
  trackAeroSyncOnSuccess: AeroSyncOnSuccessTracker;
  trackAeroSyncOnClose: AeroSyncOnCloseTracker;
  trackAeroSyncOnError: AeroSyncOnErrorTracker;
  trackPreAuthEvent: PreAuthEventTracker;
  trackEnrollmentCancelled: (currentStep: EnrollmentSteps) => void;
};
// TODO: Get dispo data here https://dutchie.atlassian.net/browse/ENG-47647
export const useDutchiePayAnalytics = (): UseDutchiePayAnalyticsResponse => {
  const router = useRouter();

  const enrollmentSource = useMemo(() => {
    const path = router?.asPath ?? '';
    return path.includes(INSTORE_ENROLLMENT_ROUTE)
      ? INSTORE_ENROLLMENT_SOURCE
      : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
        (router?.query?.enrollmentSource as string | undefined);
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
  }, [router?.asPath, router?.query?.enrollmentSource]);

  /*
    Check session storage for token, and if not found then request a new one and persist it
  */
  const dutchiePaySessionToken = getPersistedValue(DPAY_SESSION_TOKEN_KEY, '');

  // TODO: Rename the query to GetDutchiePaySessionToken https://dutchie.atlassian.net/browse/ENG-47643
  const { data, error } = useQuery(GetInstoreAnalyticsTokenDocument, { skip: Boolean(dutchiePaySessionToken) });
  const analyticsToken = data?.getInstoreAnalyticsToken?.instoreAnalyticsTokenId;

  if (error) {
    logger.error('Failed to fetch analytics token', {
      error: error.message,
      errorCode: error.graphQLErrors[0]?.extensions?.code ?? 'NO_CODE',
      enrollmentSource: enrollmentSource ?? 'unknown',
      step: 'analytics_token_fetch',
    });
  }

  if (analyticsToken) {
    setPersistedValue(DPAY_SESSION_TOKEN_KEY, analyticsToken, true);
  }

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- getPersistedValue returns '' when absent, which must fall through to the freshly-fetched analyticsToken; ?? would keep the empty string
  const eventToken = dutchiePaySessionToken || analyticsToken;

  const dutchiePayEventTracker: DutchiePayEventTracker = <K extends DutchiePayEvents>(
    methodName: K,
    ...args: Omit<Events[K], 'dutchiePaySessionToken' | 'instoreCartToken'> extends Record<string, never>
      ? []
      : [Omit<Events[K], 'dutchiePaySessionToken' | 'instoreCartToken'>]
  ) => {
    const payloadProps = args[0] ?? {};
    const payload = ({
      instoreCartToken: eventToken,
      dutchiePaySessionToken: eventToken,
      ...payloadProps,
    } as unknown) as Events[K];

    if (eventToken) {
      try {
        (tracker[methodName] as (payload: Events[K]) => void)(payload);
      } catch (err) {
        logger.error('Failed to track analytics event', {
          error: err?.message ?? 'Unknown error',
          methodName,
          hasToken: Boolean(eventToken),
          hasPayloadProps: Boolean(args[0]),
        });
      }
    }
  };

  const trackEnrollmentStepViewed: EnrollmentStepViewedTracker = useCallback(
    ({ step, context }): void =>
      tracker.dutchiePayEnrollmentStep({
        dutchiePaySessionToken: eventToken,
        step,
        action: EVENT_ACTIONS.VIEWED,
        enrollmentSource,
        context,
      }),
    [eventToken, enrollmentSource]
  );

  const trackEnrollmentStepSubmitted: EnrollmentStepSubmittedTracker = useCallback(
    ({ step, context, action = EVENT_ACTIONS.SUBMITTED }): void =>
      tracker.dutchiePayEnrollmentStep({
        dutchiePaySessionToken: eventToken,
        step,
        action,
        enrollmentSource,
        context,
      }),
    [eventToken, enrollmentSource]
  );

  const trackEnrollmentCancelled = useCallback((step: EnrollmentSteps): void => {
    tracker.dutchiePayEnrollmentStepCancelled({ step });
  }, []);

  const trackPlaidOnLoad: PlaidOnLoadTracker = useCallback(
    ({ connectionMethod, phonePassedToPlaid }) =>
      tracker.dutchiePayPlaidOnLoad({
        dutchiePaySessionToken: eventToken,
        connectionMethod,
        phonePassedToPlaid,
      }),
    [eventToken]
  );

  const trackPlaidOnSuccess: PlaidOnSuccessTracker = useCallback(
    ({ eventMetadata, connectionMethod }): void =>
      tracker.dutchiePayPlaidOnSuccess({
        dutchiePaySessionToken: eventToken,
        eventMetadata,
        connectionMethod,
      }),
    [eventToken]
  );

  const trackPlaidOnEvent: PlaidOnEventTracker = useCallback(
    ({ eventName, eventMetadata, connectionMethod }) => {
      const cleanedMetadata = _.omitBy(eventMetadata, (val) => val == null);

      tracker.dutchiePayPlaidOnEvent({
        dutchiePaySessionToken: eventToken,
        eventName,
        eventMetadata: cleanedMetadata,
        connectionMethod,
      });

      if (eventName === PlaidLinkStableEvent.ERROR) {
        logger.error('Plaid error', { eventName, eventMetadata });
      }
    },
    [eventToken]
  );

  const trackPlaidOnExit: PlaidOnExitTracker = useCallback(
    ({ plaidError, eventMetadata, connectionMethod }): void => {
      tracker.dutchiePayPlaidOnExit({
        dutchiePaySessionToken: eventToken,
        plaidError,
        eventMetadata,
        connectionMethod,
      });
    },
    [eventToken]
  );

  // TODO: Add get name of Aerosync error type and add logging
  const trackAeroSyncOnEvent: AeroSyncOnEventTracker = useCallback(
    ({ payload, type }) => {
      tracker.dutchiePayAeroSyncOnEvent({
        dutchiePaySessionToken: eventToken,
        eventName: type,
        eventMetadata: payload,
      });
    },
    [eventToken]
  );

  const trackAeroSyncOnLoad: AeroSyncOnLoadTracker = useCallback(() => {
    tracker.dutchiePayAeroSyncOnLoad({ dutchiePaySessionToken: eventToken });
  }, [eventToken]);

  const trackAeroSyncOnClose: AeroSyncOnCloseTracker = useCallback(() => {
    tracker.dutchiePayAeroSyncOnClose({ dutchiePaySessionToken: eventToken });
  }, [eventToken]);

  const trackAeroSyncOnSuccess: AeroSyncOnSuccessTracker = useCallback(
    ({ connectionId, clientName, aeroPassUserUuid }) => {
      tracker.dutchiePayAeroSyncOnSuccess({
        dutchiePaySessionToken: eventToken,
        connectionId,
        clientName,
        aeroPassUserUuid,
      });
    },
    [eventToken]
  );

  const trackAeroSyncOnError: AeroSyncOnErrorTracker = useCallback(
    (errorEvent) => {
      tracker.dutchiePayAeroSyncOnError({ dutchiePaySessionToken: eventToken, error: errorEvent });
    },
    [eventToken]
  );

  const trackPreAuthEvent: PreAuthEventTracker = useCallback(
    (eventName, eventMetadata) => {
      tracker.dutchiePayPreAuthEvent({ dutchiePaySessionToken: eventToken, eventName, eventMetadata });
    },
    [eventToken]
  );

  return {
    dutchiePayEventTracker,
    trackEnrollmentStepSubmitted,
    trackEnrollmentStepViewed,
    trackPlaidOnEvent,
    trackPlaidOnLoad,
    trackPlaidOnSuccess,
    trackPlaidOnExit,
    trackAeroSyncOnEvent,
    trackAeroSyncOnLoad,
    trackAeroSyncOnSuccess,
    trackAeroSyncOnClose,
    trackAeroSyncOnError,
    trackPreAuthEvent,
    trackEnrollmentCancelled,
  };
};
